import React, { useState, useEffect } from 'react';
import { api } from '../api';

function AddMonitorForm({ onAdded }) {
  const [monitorType, setMonitorType] = useState('company_page');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [maxPosts, setMaxPosts] = useState(5);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !url) { setError('Name and LinkedIn URL are required'); return; }
    try {
      setError('');
      await api.fetch('/api/monitors', {
        method: 'POST',
        body: JSON.stringify({ monitor_type: monitorType, name, linkedin_url: url, max_posts: maxPosts }),
      });
      setName(''); setUrl('');
      onAdded();
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="border border-slate-800 rounded-lg p-5">
      <h3 className="text-sm font-medium text-slate-200 mb-4">Add Monitor</h3>

      {error && (
        <div className="mb-3 p-2.5 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex gap-4">
          {[
            { value: 'company_page', label: 'Company Page' },
            { value: 'personal_profile', label: 'Personal Profile' },
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio" name="monitorType" value={opt.value}
                checked={monitorType === opt.value}
                onChange={(e) => setMonitorType(e.target.value)}
                className="w-3.5 h-3.5 accent-slate-400"
              />
              <span className="text-slate-400 text-sm">{opt.label}</span>
            </label>
          ))}
        </div>

        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          placeholder={monitorType === 'company_page' ? 'e.g., Alpha Level' : 'e.g., John Smith'}
          className="w-full bg-slate-800/60 text-slate-200 px-3 py-2 rounded-md border border-slate-700/50 focus:border-slate-600 focus:outline-none text-sm placeholder-slate-600"
        />

        <input
          type="url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder={monitorType === 'company_page' ? 'https://www.linkedin.com/company/...' : 'https://www.linkedin.com/in/...'}
          className="w-full bg-slate-800/60 text-slate-200 px-3 py-2 rounded-md border border-slate-700/50 focus:border-slate-600 focus:outline-none text-sm placeholder-slate-600"
        />

        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-500">Posts to scan:</label>
          <input
            type="number" min={1} max={50} value={maxPosts}
            onChange={(e) => setMaxPosts(parseInt(e.target.value) || 5)}
            className="w-16 bg-slate-800/60 text-slate-200 px-2.5 py-1.5 rounded-md border border-slate-700/50 focus:border-slate-600 focus:outline-none text-sm"
          />
        </div>

        <button
          type="submit"
          className="w-full bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium py-2 rounded-md text-sm transition-colors"
        >
          Add Monitor
        </button>
      </form>
    </div>
  );
}

function MonitorCard({ monitor, onDelete, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [maxPosts, setMaxPosts] = useState(monitor.max_posts || 5);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.fetch(`/api/monitors/${monitor.id}`, { method: 'PUT', body: JSON.stringify({ max_posts: maxPosts }) });
      setEditing(false); onUpdated?.();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-800 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2.5">
            <span className={`w-1.5 h-1.5 rounded-full ${monitor.enabled ? 'bg-emerald-400' : 'bg-slate-600'}`} />
            <h3 className="font-medium text-slate-200 text-sm">{monitor.name}</h3>
            <span className="text-[11px] text-slate-600">
              {monitor.monitor_type === 'company_page' ? 'Company' : 'Personal'}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-600">
            <span>{monitor.signal_count || 0} signals</span>
            {monitor.last_fetched_at && <span>Last: {monitor.last_fetched_at.split('T')[0]}</span>}
            <span>Every {monitor.fetch_interval_hours}h</span>
          </div>
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => setEditing(!editing)}
            className="px-2.5 py-1.5 text-slate-500 hover:text-slate-300 rounded text-xs transition-colors">
            {editing ? 'Cancel' : 'Edit'}
          </button>
          {/* "Fetch" button removed — the desktop agent pulls monitor signals
              on its own schedule using the rep's LinkedIn session. */}
          <button onClick={() => onDelete(monitor.id)}
            className="px-2.5 py-1.5 text-slate-600 hover:text-red-400 rounded text-xs transition-colors">
            Delete
          </button>
        </div>
      </div>
      {editing && (
        <div className="mt-3 pt-3 border-t border-slate-800/50 flex items-center gap-3">
          <label className="text-xs text-slate-500">Posts to scan:</label>
          <input type="number" min={1} max={50} value={maxPosts}
            onChange={(e) => setMaxPosts(parseInt(e.target.value) || 5)}
            className="w-16 bg-slate-800/60 text-slate-200 px-2.5 py-1.5 rounded-md border border-slate-700/50 focus:border-slate-600 focus:outline-none text-sm" />
          <button onClick={handleSave} disabled={saving}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

const signalTypeConfig = {
  company_follower: { label: 'Followed', color: 'text-sky-400 bg-sky-500/10' },
  company_post_like: { label: 'Liked Post', color: 'text-emerald-400 bg-emerald-500/10' },
  personal_post_like: { label: 'Liked Post', color: 'text-violet-400 bg-violet-500/10' },
  post_like: { label: 'Liked Post', color: 'text-amber-400 bg-amber-500/10' },
  post_comment: { label: 'Commented', color: 'text-orange-400 bg-orange-500/10' },
  post_reshare: { label: 'Reshared', color: 'text-pink-400 bg-pink-500/10' },
};

export default function SignalsPanel() {
  const [monitors, setMonitors] = useState([]);
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadSignals(); }, [filter]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadMonitors(), loadSignals(), loadStats()]);
    setLoading(false);
  };

  const loadMonitors = async () => {
    try { const data = await api.fetch('/api/monitors'); setMonitors(data || []); } catch (err) { console.error(err); setError(err.message || 'Failed to load monitors'); }
  };

  const loadSignals = async () => {
    try {
      const params = { limit: 100 };
      if (filter) params.signal_type = filter;
      const qs = new URLSearchParams(params).toString();
      const data = await api.fetch(`/api/signals?${qs}`);
      setSignals(data || []);
    } catch (err) { console.error(err); setError(err.message || 'Failed to load signals'); }
  };

  const loadStats = async () => {
    try { const data = await api.fetch('/api/signals/stats'); setStats(data); } catch (err) { console.error(err); }
  };

  const handleDeleteMonitor = async (id) => {
    if (!window.confirm('Delete this monitor?')) return;
    try { await api.fetch(`/api/monitors/${id}`, { method: 'DELETE' }); await loadMonitors(); } catch (err) { setError(err.message); }
  };

  return (
    <div className="h-full overflow-y-auto scrollable-content">
      <div className="max-w-4xl mx-auto px-8 py-6 space-y-8">
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm">{error}</div>
        )}
        {successMsg && (
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-md text-emerald-400 text-sm">{successMsg}</div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {[
              { value: stats.total, label: 'Total Signals', color: 'text-slate-100' },
              { value: stats.by_type?.find(t => t.type === 'company_follower')?.count || 0, label: 'Followers', color: 'text-sky-400' },
              { value: (stats.by_type?.find(t => t.type === 'company_post_like')?.count || 0) + (stats.by_type?.find(t => t.type === 'personal_post_like')?.count || 0) + (stats.by_type?.find(t => t.type === 'post_like')?.count || 0), label: 'Post Likes', color: 'text-emerald-400' },
              { value: stats.by_type?.find(t => t.type === 'post_comment')?.count || 0, label: 'Comments', color: 'text-orange-400' },
              { value: stats.by_type?.find(t => t.type === 'post_reshare')?.count || 0, label: 'Reshares', color: 'text-pink-400' },
              { value: stats.matched_leads, label: 'Matched', color: 'text-violet-400' },
            ].map((s, i) => (
              <div key={i} className="border border-slate-800 rounded-lg p-4">
                <div className={`text-2xl font-semibold tabular-nums ${s.color}`}>{s.value}</div>
                <div className="text-[11px] text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Pipeline actions moved to the desktop agent. The agent handles
            promotion + profile enrichment locally (so it can use the rep's
            LinkedIn session). Leaving an info note here so reps know where
            to look. */}
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-md p-3 text-xs text-slate-400">
          Signal scraping, promotion, and profile enrichment now run inside the desktop
          Siphon Agent so they use the rep's own LinkedIn session.
        </div>

        {/* Monitors */}
        <div>
          <h2 className="text-sm font-medium text-slate-200 mb-3">Monitors ({monitors.length})</h2>
          <div className="space-y-2 mb-4">
            {monitors.map(m => (
              <MonitorCard key={m.id} monitor={m} onDelete={handleDeleteMonitor} onUpdated={loadMonitors} />
            ))}
          </div>
          <AddMonitorForm onAdded={() => { loadMonitors(); }} />
        </div>

        {/* Post scraper moved to the desktop agent. */}

        {/* Signal Feed */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-slate-200">Signal Feed</h2>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-slate-800/60 text-slate-400 px-2.5 py-1.5 rounded-md border border-slate-700/50 text-xs focus:outline-none"
            >
              <option value="">All Types</option>
              <option value="company_follower">Followers</option>
              <option value="company_post_like">Company Post Likes</option>
              <option value="personal_post_like">Personal Post Likes</option>
              <option value="post_like">Post Likes</option>
              <option value="post_comment">Post Comments</option>
              <option value="post_reshare">Post Reshares</option>
            </select>
          </div>

          {signals.length === 0 ? (
            <div className="text-center py-12 text-slate-600 text-sm">
              <p>No signals captured yet.</p>
              <p className="mt-1">Add a monitor above — the desktop Siphon Agent pulls signals on its own schedule using your LinkedIn session.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {signals.map(sig => {
                const cfg = signalTypeConfig[sig.signal_type] || { label: sig.signal_type, color: 'text-slate-400 bg-slate-500/10' };
                return (
                  <div key={sig.id} className="flex items-start gap-3 px-4 py-3 rounded-md hover:bg-slate-800/30 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-200">{sig.linkedin_name}</span>
                        <span className={`text-[11px] px-1.5 py-0.5 rounded ${cfg.color}`}>{cfg.label}</span>
                        {sig.lead_id && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">Matched</span>
                        )}
                      </div>
                      {sig.linkedin_headline && (
                        <div className="text-xs text-slate-600 mt-0.5 truncate">{sig.linkedin_headline}</div>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-600">
                        <span>{sig.target_label}</span>
                        {sig.post_text && <span className="truncate max-w-xs">- {sig.post_text}</span>}
                        <span className="ml-auto flex-shrink-0">{sig.captured_at?.split('T')[0]}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
